import {
  ColumnFiltersState,
  createColumnHelper,
  OnChangeFn,
  RowSelectionState,
} from "@tanstack/react-table";
import { EditorIcon, IconButton, TrashcanIcon } from "@theprelude/ds";
import { useOverlay } from "@/hooks/use-overlay";
import { ThreatRow } from "@/queries/schedule";
import {
  checkboxColumn,
  frequencyColumn,
  scheduledColumn,
  sourceIdColumn,
  targetColumn,
  threatIdColumn,
  threatNameColumn,
} from "../table/columns";
import { useTable } from "../table/hooks";
import { VirtualTable } from "../table/virtual-table";
import { ActionBar } from "./action-bar";

function ActionsColumn({
  id,
  isScheduled,
}: {
  id: string;
  isScheduled: boolean;
}) {
  const overlay = useOverlay();

  if (!isScheduled) {
    return null;
  }

  return (
    <div className="inline-flex w-full items-center justify-end gap-2">
      <IconButton
        icon={<EditorIcon />}
        onClick={() => {
          void overlay.open("schedule", { ids: [id], type: "threat" });
        }}
      />
      <IconButton
        data-testid="schedule-table-delete"
        icon={<TrashcanIcon />}
        onClick={() => {
          void overlay.open("deleteSchedule", { ids: [id], type: "threat" });
        }}
      />
    </div>
  );
}

const columnHelper = createColumnHelper<ThreatRow>();
const columns = [
  columnHelper.display(checkboxColumn),
  columnHelper.accessor("threatId", threatIdColumn),
  columnHelper.accessor("source_id", sourceIdColumn),
  columnHelper.accessor("threat", threatNameColumn),
  columnHelper.accessor("frequency", frequencyColumn),
  columnHelper.accessor("target", targetColumn),
  columnHelper.accessor("isScheduled", scheduledColumn),
  columnHelper.display({
    id: "actions",
    enableHiding: false,
    cell: (info) => {
      return (
        <ActionsColumn
          id={info.row.original.rowId}
          isScheduled={info.row.original.isScheduled}
        />
      );
    },
    meta: {
      widths: ["50px", "50px"],
    },
  }),
];

export function ThreatsTable({
  threats,
  columnFilters,
  rowSelection,
  setRowSelection,
}: {
  threats: ThreatRow[];
  columnFilters: ColumnFiltersState;
  rowSelection: Record<string, boolean>;
  setRowSelection: OnChangeFn<RowSelectionState>;
}) {
  const table = useTable({
    name: "schedule-threats",
    data: threats,
    columns,
    initialState: {
      columnVisibility: { [threatIdColumn.id]: false },
    },
    state: {
      columnFilters,
      rowSelection,
    },
    getRowId: (row) => row.rowId,
    onRowSelectionChange: setRowSelection,
  });

  return (
    <>
      <VirtualTable showFilter table={table} withActionBar />
      <ActionBar
        selectedRows={table.getSelectedRowModel().flatRows}
        onCancel={() => {
          table.resetRowSelection();
        }}
        type="threat"
      />
    </>
  );
}
